/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {Client, RestResponse} from '../model';
import {SearchableProvider} from '../model/interface/searchable.provider';
import {ApiHeaders} from './headers';

@Injectable()
export class ClientProvider implements SearchableProvider {

  constructor(
    private http: HttpClient
  ) {}

  search(query?: string, id?: string, nonBank?: boolean, lsp?: boolean, page?: number): Observable<RestResponse> {
    return this.getClients(page ? page : 1, query);
  }

  getFilteredClients(page: number, sort: string, order: string, query?: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/client/list?page=${page}&sort=${sort}&order=${order}` +
      (query ? '&query=' + encodeURIComponent(query) : ''),
      ApiHeaders.getOptions());
  }

  getClients(page: number, query?: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/client/list?page=${page}&sort=COMPANY_NAME&order=ASC` +
        (query ? '&query=' + encodeURIComponent(query) : ''),
      ApiHeaders.getOptions());
  }

  getExportClients(page: number, sort:string,order:string,query?: string): Observable<any> {
    return this.http
      .get(`${environment.API_URL}/api/client/user/list/export?page=${page}&sort=${sort}&order=${order}` +
        (query ? '&query=' + encodeURIComponent(query) : ''),
      ApiHeaders.getOptionsJsonPlain());
  }

  saveClient(client: Client): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/client`, client, ApiHeaders.getOptions());
  }

  getClientUsers(page: number, sort: string, order: string, query?: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/client/user/list?page=${page}&sort=${sort}&order=${order}` +
      (query ? '&query=' + encodeURIComponent(query) : ''),
      ApiHeaders.getOptions());
  }

  getClient(id: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/client/${id}`, ApiHeaders.getOptions());
  }

  deleteClient(client: Client): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/client/${client.ID}/delete`, null, ApiHeaders.getOptions());
  }

  save(id: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/client/${id}/field`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  getClientsByDomain(query: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/client/list/domain?query=${encodeURIComponent(query)}`,
        ApiHeaders.getOptions());
  }

  getSearchType(): string {
    return 'CLIENT';
  }
}
